import type { OrderSearchFilters } from '~/components/Common/Forms/OrderSearchForm';

import { filterFields } from '~/consts/orderFilter';

const SEARCH_FIELDS = [
  'universal_search_id',
  'customer_details',
  'sold_by',
  'note_comment',
  ...filterFields({ showMultiSearch: true, showTravellerNameFilter: true }).map(({ key }) => key),
];

const ADDITIONAL_PARAMS = {
  startDate: 'start_date',
  endDate: 'end_date',
  itemType: 'item_type',
  offerId: 'offer_id',
  checkInDate: 'check_in_date',
  checkOutDate: 'check_out_date',
} as const;

export function parseOrderSearchParams(params: URLSearchParams): OrderSearchFilters | undefined {
  const field = params.get('filter_field');
  if (!SEARCH_FIELDS.includes(field)) {
    return undefined;
  }

  const additionalParams = Object.fromEntries(
    Object.entries(ADDITIONAL_PARAMS).map(([key, param]) => [key, params.get(param) ?? '']),
  );

  return { field, value: params.get('filter_value') ?? '', additionalParams };
}

/** Replace the submitted search, retaining unrelated URL state and returning to the first page. */
export function buildOrderSearchParams(
  currentParams: URLSearchParams,
  filters: OrderSearchFilters,
  businessId?: string | null,
): URLSearchParams {
  const params = new URLSearchParams(currentParams);
  ['filter_field', 'filter_value', 'customer_id', 'business_id', 'page', ...Object.values(ADDITIONAL_PARAMS)].forEach(
    (key) => params.delete(key),
  );

  if (SEARCH_FIELDS.includes(filters.field)) {
    params.set('filter_field', filters.field);
    if (filters.value) {
      params.set('filter_value', filters.value);
    }
    const keys =
      filters.field === 'created_at'
        ? (['startDate', 'endDate'] as const)
        : filters.field === 'multi_search'
          ? (['itemType', 'offerId', 'checkInDate', 'checkOutDate'] as const)
          : [];
    keys.forEach((key) => {
      const value = filters.additionalParams[key];
      if (value) {
        params.set(ADDITIONAL_PARAMS[key], value);
      }
    });
  }

  if (businessId) {
    params.set('business_id', businessId);
  }
  return params;
}
